import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { SectionIntro } from '../../../openapi/atlantis';
import { OnboardingResponseRoleEnum } from '../../../openapi/yenta/index.ts';
import ErrorService from '../../../services/ErrorService.ts';
import { useAppSelector } from '../../../slices/store';
import {
  AnalyticsEventEnum,
  ConfigDrivenIntroTemplate,
  PartialEnumMap,
} from '../../../types.ts';
import { ApplicationStepWithNewContent } from '../../../utils/ConfigDrivenFormUtils';
import { safePartialEnumMapGet } from '../../../utils/EnumHelper';
import Logger from '../../../utils/Logger';
import { getLegacyApplicationId } from '../../../utils/OnboardingHelpers';
import { removeHtmlTagsFromString } from '../../../utils/StringUtils.ts';
import AnalyticsEventOnLoad from '../../Analytics/AnalyticsEventOnLoad.tsx';
import ConfigDrivenContainerLayoutV2 from '../../Layout/ConfigDrivenContainerLayoutV2.tsx';
import ConfigDrivenFormFooter from '../Form/V2/ConfigDrivenFormFooter.tsx';
import AgentConfigDrivenSubtitle from '../Misc/AgentConfigDrivenSubtitle.tsx';
import AgentConfigDrivenTitle from '../Misc/AgentConfigDrivenTitle.tsx';
import ConfigDrivenSubtitleV2 from '../Misc/ConfigDrivenSubtitleV2.tsx';
import ConfigDrivenTitleV2 from '../Misc/ConfigDrivenTitleV2.tsx';

interface ConfigDrivenIntroContainerV2Props {
  config: ApplicationStepWithNewContent<SectionIntro>;
  onStart: (stepId: string) => void;
  onNext: () => void;
}

// Component maps for template-specific components
const TITLE_COMPONENTS: PartialEnumMap<
  ConfigDrivenIntroTemplate,
  React.FC<{ title: string; textStyle?: string }>
> = {
  [ConfigDrivenIntroTemplate.TEMPLATE_3]: AgentConfigDrivenTitle,
};

const SUBTITLE_COMPONENTS: PartialEnumMap<
  ConfigDrivenIntroTemplate,
  React.FC<{ subtitle: string; textStyle?: string }>
> = {
  [ConfigDrivenIntroTemplate.TEMPLATE_3]: AgentConfigDrivenSubtitle,
};

const ConfigDrivenIntroContainerV2 = ({
  config,
  onStart,
  onNext,
}: ConfigDrivenIntroContainerV2Props) => {
  const { applicationId } = useParams();
  const { authUserDetail } = useAppSelector((state) => state.auth);

  const appId = useMemo(
    () =>
      applicationId ||
      getLegacyApplicationId(authUserDetail, OnboardingResponseRoleEnum.Agent),
    [authUserDetail, applicationId],
  );
  const methods = useForm();

  const { id, title, subtitle } = config.content || {};

  // Select appropriate components based on template
  const TitleComponent = safePartialEnumMapGet(
    TITLE_COMPONENTS,
    config?.content?.style?.template as unknown as ConfigDrivenIntroTemplate,
    ConfigDrivenTitleV2,
  );

  const SubtitleComponent = safePartialEnumMapGet(
    SUBTITLE_COMPONENTS,
    config?.content?.style?.template as unknown as ConfigDrivenIntroTemplate,
    ConfigDrivenSubtitleV2,
  );

  const handleOnSubmit = async () => {
    try {
      await onStart(id!);
      await onNext();
    } catch (e: any) {
      Logger.error('Failed to submit answer:', e);
      ErrorService.notify('Failed to submit answer', e);
    }
  };

  return (
    <ConfigDrivenContainerLayoutV2 config={config}>
      <div className='flex flex-col flex-grow sm:justify-center justify-start items-start w-full sm:w-[524px] mx-auto mb-10'>
        {!!title && <TitleComponent title={title} />}
        {!!subtitle && (
          <div className='pt-3'>
            <SubtitleComponent subtitle={subtitle} />
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <ConfigDrivenFormFooter
          config={config}
          onSubmit={handleOnSubmit}
          applicationId={appId}
        />
      </FormProvider>
      <AnalyticsEventOnLoad
        eventName={AnalyticsEventEnum.SECTION_INTRO_VIEWED}
        eventData={{
          section: removeHtmlTagsFromString(title!),
        }}
      />
    </ConfigDrivenContainerLayoutV2>
  );
};

export default ConfigDrivenIntroContainerV2;
